<template>
  <div class="interstitial-page">
    <!-- Отображаем заголовок урока из загруженных данных или общий заголовок -->
    <h3 v-if="lessonContent && lessonContent.title" class="lesson-main-title">{{ lessonContent.title }}</h3>
    <h4 v-else-if="!isLoading">Информационная страница (Урок)</h4>
    
    <p v-if="pageKey" class="pagekey-info">Ключ контента: <strong>{{ pageKey }}</strong></p>

    <div v-if="isLoading" class="loading-state">
      <p>Загрузка содержимого урока...</p>
    </div>
    <div v-else-if="error" class="error-state">
      <p class="error-message">Ошибка: {{ error }}</p>
      <button @click="fetchLessonContent(pageKey)" class="retry-button">Попробовать снова</button>
    </div>
    <div v-else-if="lessonContent && lessonContent.sections && lessonContent.sections.length" class="content-display">
      <div v-for="(section, index) in lessonContent.sections" :key="index" class="lesson-section">
        <h5 v-if="section.title" class="section-title">{{ section.title }}</h5>
        <p v-for="(paragraph, pIndex) in section.paragraphs" :key="pIndex" class="lesson-paragraph">
          {{ paragraph }}
        </p>
      </div>
    </div>
    <div v-else-if="!isLoading && pageKey"> <!-- Сообщение, если контент не найден или пуст -->
      <p class="no-content-message"><em>Контент для ключа '{{ pageKey }}' не загружен, отсутствует или имеет неверный формат.</em></p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InterstitialPage',
  props: {
    pageKey: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      lessonContent: null,
      isLoading: false,
      error: null,
    };
  },
  methods: {
    async fetchLessonContent(key) {
      if (!key) {
        this.lessonContent = null;
        this.error = "Ключ страницы не предоставлен.";
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      this.error = null;
      // Не сбрасываем lessonContent немедленно, чтобы избежать "мелькания" интерфейса
      // this.lessonContent = null; 
      try {
        const response = await fetch(`/api/content/${key}`); // Используем относительный путь
        if (!response.ok) {
          let errorDetail = response.statusText;
          try {
            // Попытка получить детали ошибки из JSON ответа, если бэкенд их предоставляет
            const errorData = await response.json();
            errorDetail = errorData.detail || errorDetail;
          } catch (e) {
            // Игнорируем ошибку парсинга JSON, если тело ответа не JSON
          }
          throw new Error(`Ошибка HTTP: ${response.status} - ${errorDetail}`);
        }
        const data = await response.json();
        // Простая проверка, что полученные данные содержат ожидаемую структуру
        if (data && Array.isArray(data.sections)) {
            this.lessonContent = data;
                } else {
          throw new Error("Полученные данные имеют неверный формат или отсутствуют секции.");
        }
      } catch (e) {
        this.error = `${e.message}`; // Отображаем только сообщение об ошибке без деталей ключа, т.к. ключ уже есть на странице
        this.lessonContent = null; // Очищаем контент в случае ошибки
      } finally {
        this.isLoading = false;
      }
    }
  },
  created() {
    // Загружаем контент при создании компонента
    this.fetchLessonContent(this.pageKey);
  },
  watch: {
    // Следим за изменениями pageKey (например, при навигации на другой урок без перезагрузки компонента)
    pageKey(newPageKey, oldPageKey) {
      if (newPageKey !== oldPageKey && newPageKey) {
        this.fetchLessonContent(newPageKey);
      }
    }
  }
}
</script>

<style scoped>
.interstitial-page {
  padding: 20px;
  border: 1px solid #e0e0e0; /* Мягкая рамка */
  border-radius: 8px;
  background-color: #ffffff; /* Чистый фон */
  margin-top: 20px;
  margin-bottom: 20px;
  text-align: left;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.pagekey-info {
  font-size: 0.9em;
  color: #757575;
  margin-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
}

.lesson-main-title { 
  font-size: 1.8em; /* Немного уменьшил для соответствия "Информационная страница" */
  color: #333; 
  margin-top: 0;
  margin-bottom: 15px; 
}

.interstitial-page h4 { /* Заголовок по умолчанию, если lessonContent.title нет */
  margin-top: 0;
  color: #4682B4; 
  margin-bottom: 10px;
  font-size: 1.5em;
}

.loading-state p,
.no-content-message { 
  margin-top: 10px; 
  font-style: italic;
  color: #757575;
  padding: 15px;
  background-color: #f9f9f9;
  border-left: 3px solid #007bff;
}

.error-state {
  margin-top: 10px;
  padding: 15px;
  background-color: #fff0f0;
  border-left: 3px solid #d9534f;
  color: #d9534f;
}
.error-message {
  margin-bottom: 10px;
  font-weight: bold;
}
.retry-button { 
  padding: 8px 15px; 
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}
.retry-button:hover {
  background-color: #0056b3;
}

.content-display { 
  margin-top: 15px; 
}
.lesson-section { 
  margin-bottom: 25px; 
  padding-left: 15px; /* Отступ для визуального выделения секции */
  border-left: 3px solid #7baaf7; /* Светло-голубая линия для секции */
}
.section-title { 
  font-size: 1.4em; /* Увеличил для читаемости */
  color: #333; 
  margin-top: 0;
  margin-bottom: 12px; 
}
.lesson-paragraph { 
  margin-bottom: 10px; 
  line-height: 1.6; /* Стандартный интерлиньяж для текста */
  color: #444; /* Чуть темнее для основного текста */
  text-align: justify;
  hyphens: auto; /* Автоматические переносы */
}

/* Стили для ссылок и выделений, если они будут в тексте с бэкенда */
.content-display a {
  color: #007bff;
  text-decoration: none;
}
.content-display a:hover {
  text-decoration: underline;
}
.content-display strong {
  font-weight: bold;
  color: #222;
}
.content-display em {
  font-style: italic;
  color: #555;
}
</style> 